// src/services/pagamentoService.js

import api from './api';

// Validações comuns
const validarNumeroTelefone = (numero) => {
  if (!numero) return false;
  const numeroLimpo = numero.replace(/\D/g, '');
  return /^84\d{7}$/.test(numeroLimpo);
};

const formatarNumeroTelefone = (numero) => {
  if (!numero) return null;
  return numero.replace(/\D/g, '');
};

const validarDadosPagamento = (dadosPagamento, { isPublic = false } = {}) => {
  // Validações básicas
  if (!dadosPagamento.prestacaoId) {
    throw new Error('ID da prestação é obrigatório');
  }

  if (!dadosPagamento.metodoPagamento) {
    throw new Error('Método de pagamento é obrigatório');
  }

  if (!dadosPagamento.valorPago || dadosPagamento.valorPago <= 0) {
    throw new Error('Valor do pagamento inválido');
  }

  if (isPublic && !dadosPagamento.token) {
    throw new Error('Token de acesso é obrigatório para pagamento público');
  }

  // Validação específica por método de pagamento
  if (['MPESA', 'EMOLA'].includes(dadosPagamento.metodoPagamento)) {
    const numeroTelefone = dadosPagamento.dadosPagamento?.numeroTelefone;
    if (!numeroTelefone) {
      throw new Error('Número de telefone é obrigatório para pagamentos móveis');
    }
    if (!validarNumeroTelefone(numeroTelefone)) {
      throw new Error('Número de telefone inválido. Use o formato: 84XXXXXXX');
    }
    dadosPagamento.dadosPagamento.numeroTelefone = formatarNumeroTelefone(numeroTelefone);
  } 
  else if (['BCI', 'MOZABANK'].includes(dadosPagamento.metodoPagamento)) {
    if (!dadosPagamento.dadosPagamento?.entidade) {
      throw new Error('Entidade é obrigatória para pagamentos bancários');
    }
    if (!dadosPagamento.dadosPagamento?.referencia) {
      throw new Error('Referência é obrigatória para pagamentos bancários');
    }
  } 
  else {
    throw new Error('Método de pagamento não suportado');
  }

  return dadosPagamento;
};

// Funções principais
export const realizarPagamento = async (dadosPagamento) => {
  try {
    console.log('[PagamentoService] Iniciando pagamento:', dadosPagamento);
    const dadosValidados = validarDadosPagamento(dadosPagamento);
    const response = await api.post('/pagamentos', dadosValidados);
    console.log('[PagamentoService] Resposta do pagamento:', response.data);
    const pagamentoId = response.data?.data?.pagamento?.id;
    if (!pagamentoId) {
      throw new Error('ID do pagamento não encontrado na resposta');
    }
    return {
      ...response.data.data.pagamento,
      id: pagamentoId
    };
  } catch (error) {
    console.error('[PagamentoService] Erro ao realizar pagamento:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao processar pagamento'
    );
  }
};

export const gerarTokenPagamentoPublico = async (prestacaoId) => {
  try {
    const response = await api.post(`/pagamentos/${prestacaoId}/gerar-token-publico`);
    return response.data;
  } catch (error) {
    console.error('Erro ao gerar token público:', error);
    throw error;
  }
};

export const processarPagamento = async (prestacaoId, dados, { isPublic = false } = {}) => {
  try {
    const endpoint = isPublic 
      ? `/pagamento-publico/${prestacaoId}/pagamento`
      : `/pagamentos/processar/${prestacaoId}`;
    const response = await api.post(endpoint, dados);
    return response.data;
  } catch (error) {
    console.error('Erro ao processar pagamento:', error);
    throw error;
  }
};

export const validarDadosPagamentoService = (dados, options) => {
  return validarDadosPagamento(dados, options);
};

export const verificarStatusPagamento = async (pagamentoId, { isPublic = false } = {}) => {
  try {
    const endpoint = isPublic 
      ? `/pagamento-publico/pagamento/${pagamentoId}/status`
      : `/pagamentos/${pagamentoId}/status`;
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar status:', error);
    throw error;
  }
};

export const registrarTentativaPagamentoPublico = async (prestacaoId, token) => {
  try {
    const response = await api.post(
      `/pagamento-publico/${prestacaoId}/registrar-tentativa`,
      null,
      { params: { token } }
    );
    return response.data;
  } catch (error) {
    console.error('Erro ao registrar tentativa:', error);
    throw error;
  }
};

// Função para verificar status após webhook
export const verificarStatusWebhook = async (metodoPagamento, pagamentoId) => {
  try {
    console.log(`[PagamentoService] Verificando status webhook ${metodoPagamento} para pagamento:`, pagamentoId);
    let endpoint = '';
    switch (metodoPagamento) {
      case 'MPESA':
        endpoint = '/webhook/mpesa/status';
        break;
      case 'EMOLA':
        endpoint = '/webhook/emola/status';
        break;
      case 'BIM':
        endpoint = '/webhook/bim/status';
        break;
      case 'BCI':
        endpoint = '/webhook/bci/status';
        break;
      case 'STANDARDBANK':
        endpoint = '/webhook/standardbank/status';
        break;
      case 'MOZABANK':
        endpoint = '/webhook/mozabank/status';
        break;
      case 'CARTAO':
        endpoint = '/webhook/cartao/status';
        break;
      default:
        throw new Error('Método de pagamento não suportado');
    }
    const response = await api.get(`${endpoint}/${pagamentoId}`);
    return response.data;
  } catch (error) {
    console.error(`[PagamentoService] Erro ao verificar status webhook:`, error);
    throw error;
  }
};

export const iniciarVerificacaoPeriodica = (
  pagamentoId,
  metodoPagamento,
  {
    onSuccess,
    onError,
    onTimeout,
    onStatusChange
  },
  config = {
    intervalo: 10000,
    maxTentativas: 12
  }
) => {
  if (!pagamentoId) {
    console.error('ID do pagamento não fornecido para verificação periódica');
    onError?.(new Error('ID do pagamento não fornecido'));
    return () => {};
  }

  let tentativas = 0;
  
  console.log(`[PagamentoService] Iniciando verificação periódica para pagamento ${pagamentoId} via ${metodoPagamento}`);

  const verificador = setInterval(async () => {
    try {
      const response = await verificarStatusWebhook(metodoPagamento, pagamentoId);
      onStatusChange?.(response.status);
      if (response.status === 'CONFIRMADO') {
        clearInterval(verificador);
        onSuccess?.(response);
        return;
      }
      if (response.status === 'REJEITADO') {
        clearInterval(verificador);
        onError?.(new Error(response.mensagem || 'Pagamento rejeitado'));
        return;
      }
      tentativas++;
      console.log(`Tentativa ${tentativas} de ${config.maxTentativas}`);
      if (tentativas >= config.maxTentativas) {
        clearInterval(verificador);
        onTimeout?.();
      }
    } catch (error) {
      clearInterval(verificador);
      onError?.(error);
    }
  }, config.intervalo);

  return () => clearInterval(verificador);
};

export const listarPagamentos = async (filtros = {}) => {
  try {
    const response = await api.get('/pagamentos', { params: filtros });
    console.log('Resposta da listagem de pagamentos:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao listar pagamentos:', error);
    throw new Error(error.response?.data?.message || 'Erro ao listar pagamentos');
  }
};

export const obterPagamento = async (id) => {
  try {
    if (!id) {
      throw new Error('ID do pagamento é obrigatório');
    }
    const response = await api.get(`/pagamentos/${id}`);
    console.log('Resposta do pagamento obtido:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao obter pagamento:', error);
    throw new Error(error.response?.data?.message || 'Erro ao obter pagamento');
  }
};

export const confirmarPagamento = async (id, dadosConfirmacao) => {
  try {
    if (!id) {
      throw new Error('ID do pagamento é obrigatório');
    }
    if (!dadosConfirmacao) {
      throw new Error('Dados de confirmação são obrigatórios');
    }
    const response = await api.post(`/pagamentos/${id}/confirmar`, dadosConfirmacao);
    console.log('Resposta da confirmação do pagamento:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao confirmar pagamento:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao confirmar pagamento'
    );
  }
};

export const rejeitarPagamento = async (id, motivoRejeicao) => {
  try {
    if (!id) {
      throw new Error('ID do pagamento é obrigatório');
    }
    if (!motivoRejeicao) {
      throw new Error('Motivo da rejeição é obrigatório');
    }
    const response = await api.post(`/pagamentos/${id}/rejeitar`, { motivoRejeicao });
    console.log('Resposta da rejeição do pagamento:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao rejeitar pagamento:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao rejeitar pagamento'
    );
  }
};

export const verificarPagamentosMobile = async () => {
  try {
    const response = await api.post('/pagamentos/verificar-mobile');
    console.log('Resposta da verificação de pagamentos móveis:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar pagamentos móveis:', error);
    throw new Error(error.response?.data?.message || 'Erro ao verificar pagamentos móveis');
  }
};

export const listarPlanosPagamentoEstudante = async () => {
  try {
    const response = await api.get('/planos-pagamento/estudante/meus-planos');
    if (!response.data) {
      throw new Error('Dados não recebidos da API');
    }
    console.log('Resposta da listagem de planos:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro detalhado do serviço:', error);
    if (error.response) {
      throw new Error(error.response.data?.message || 'Erro ao listar planos de pagamento');
    }
    throw new Error('Erro de conexão com o servidor');
  }
};

export const calcularValorComMulta = async (prestacaoId) => {
  try {
    const response = await api.get(`/pagamentos/${prestacaoId}/calcular-multa`);
    console.log('[PagamentoService] Cálculo de multa:', response.data);
    return response.data.data;
  } catch (error) {
    console.error('[PagamentoService] Erro ao calcular multa:', error);
    throw new Error(error.response?.data?.message || 'Erro ao calcular valor com multa');
  }
};

export const downloadComprovativo = async (pagamentoId) => {
  try {
    const response = await api.get(`/pagamentos/${pagamentoId}/comprovativo`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao baixar comprovativo:', error);
    throw new Error(error.response?.data?.message || 'Erro ao baixar comprovativo');
  }
};

export const gerarFaturaProforma = async (prestacaoId) => {
  try {
    console.log('[PagamentoService] Gerando fatura proforma:', prestacaoId);
    if (!prestacaoId) {
      throw new Error('ID da prestação é obrigatório');
    }
    const response = await api.get(`/pagamentos/fatura-proforma/${prestacaoId}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
        'Content-Type': 'application/pdf'
      }
    });
    return response;
  } catch (error) {
    console.error('[PagamentoService] Erro ao gerar fatura proforma:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao gerar fatura proforma'
    );
  }
};

export const obterDadosFaturaProforma = async (prestacaoId) => {
  try {
    console.log('[PagamentoService] Obtendo dados da fatura proforma:', prestacaoId);
    const response = await api.get(`/pagamentos/fatura-proforma/${prestacaoId}/dados`);
    return response.data;
  } catch (error) {
    console.error('[PagamentoService] Erro ao obter dados da fatura:', error);
    throw error;
  }
};

export const listarMetodosPagamento = async (prestacaoId) => {
  try {
    console.log('[PagamentoService] Listando métodos de pagamento:', prestacaoId);
    const response = await api.get(`/pagamentos/metodos-pagamento/${prestacaoId}`);
    return response.data;
  } catch (error) {
    console.error('[PagamentoService] Erro ao listar métodos de pagamento:', error);
    throw error;
  }
};

export const sendPaymentEmail = async (prestacaoId, type) => {
  try {
    console.log(`[PagamentoService] Enviando email ${type} para prestação:`, prestacaoId);
    if (!prestacaoId || !type) {
      throw new Error('ID da prestação e tipo de email são obrigatórios');
    }
    const response = await api.post(`/pagamentos/${prestacaoId}/email/${type}`);
    console.log('[PagamentoService] Email enviado com sucesso:', response.data);
    return response.data;
  } catch (error) {
    console.error('[PagamentoService] Erro ao enviar email:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao enviar email'
    );
  }
};

export const getEmailHistory = async (prestacaoId) => {
  try {
    console.log('[PagamentoService] Obtendo histórico de emails:', prestacaoId);
    const response = await api.get(`/pagamentos/${prestacaoId}/emails`);
    return response.data;
  } catch (error) {
    console.error('[PagamentoService] Erro ao obter histórico de emails:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao obter histórico de emails'
    );
  }
};

export const checkEmailStatus = async (prestacaoId) => {
  try {
    console.log(`[PagamentoService] Verificando status do email para a prestação:`, prestacaoId);
    if (!prestacaoId) {
      throw new Error('ID da prestação é obrigatório para verificar status do email');
    }
    const response = await api.get(`/pagamentos/${prestacaoId}/email/status`);
    return response.data;
  } catch (error) {
    console.error('[PagamentoService] Erro ao verificar status do email:', error);
    throw new Error(
      error.response?.data?.message || 
      'Erro ao verificar status do email'
    );
  }
};

// Exportações
const pagamentoService = {
  realizarPagamento,
  gerarTokenPagamentoPublico,
  processarPagamento,
  validarDadosPagamentoService,
  verificarStatusPagamento,
  registrarTentativaPagamentoPublico,
  verificarStatusWebhook,
  iniciarVerificacaoPeriodica,
  listarPagamentos,
  obterPagamento,
  confirmarPagamento,
  rejeitarPagamento,
  verificarPagamentosMobile,
  listarPlanosPagamentoEstudante,
  calcularValorComMulta,
  downloadComprovativo,
  gerarFaturaProforma,
  obterDadosFaturaProforma,
  listarMetodosPagamento,
  sendPaymentEmail,
  getEmailHistory,
  checkEmailStatus
};

export default pagamentoService;
